import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Plus, RefreshCw, Trash2, Wallet } from 'lucide-react';
import { apiClient } from '../config/apiConfig';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';

const emptyForm = {
  label: '',
  bankName: '전북은행',
  accountNumber: '',
  accountHolder: '카이로스',
  purpose: 'marketplace_receive',
  linkedUserId: '',
  isActive: true,
};

const purposeLabel = {
  marketplace_receive: '직영 판매 수취',
  fee_settle: '중계 수수료 수취',
};

const PlatformAccountsPanel = () => {
  const { showToastMessage } = useToastMessage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [active, setActive] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/marketplace/platform-accounts');
      const data = res.data || res;
      setAccounts(data.accounts || []);
      setLedger(data.ledger || []);
      setActive(data.active || {});
    } catch (error) {
      console.error(error);
      showToastMessage?.(error.response?.data?.message || '계좌 목록 조회 실패', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToastMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post('/api/marketplace/platform-accounts', {
        ...form,
        linkedUserId: form.linkedUserId?.trim() || null,
      });
      showToastMessage?.('계좌가 등록되었습니다', 'success');
      setForm(emptyForm);
      setShowForm(false);
      await load();
    } catch (error) {
      showToastMessage?.(error.response?.data?.message || '등록 실패', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('이 계좌를 비활성화할까요?')) return;
    try {
      await apiClient.delete(`/api/marketplace/platform-accounts/${id}`);
      showToastMessage?.('비활성화되었습니다', 'success');
      await load();
    } catch (error) {
      showToastMessage?.(error.response?.data?.message || '처리 실패', 'error');
    }
  };

  const formatTs = (v) => {
    if (!v) return '—';
    try {
      return new Date(v).toLocaleString('ko-KR');
    } catch {
      return String(v);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        <p className="font-semibold mb-1">중고장터·수수료 수취 계좌</p>
        <p>
          · <strong>직영 판매 수취</strong>: 카이로스 직영 매물 대금이 입금됩니다 (앱에 계좌 노출).
          <br />
          · <strong>중계 수수료 수취</strong>: 유저 거래완료 시 판매가 3%가 판매자 지갑에서 이 계좌로
          이체됩니다. 없으면 수취 계좌로 폴백합니다.
          <br />· 잔액은 카이로스 장부(및 연결 유저 VA) 기준입니다. 헥토 VA↔VA LIVE API는 별도 확보
          후 교체합니다.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase">
            <Wallet size={14} /> 활성 직영 수취
          </div>
          {active.marketplace_receive ? (
            <div className="mt-2">
              <p className="font-semibold text-slate-900">
                {active.marketplace_receive.bankName}{' '}
                <span className="font-mono">{active.marketplace_receive.accountNumber}</span>
              </p>
              <p className="text-sm text-slate-500">
                {active.marketplace_receive.accountHolder} · 잔액 ₩
                {Number(active.marketplace_receive.lastBalance || 0).toLocaleString()}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-sm text-rose-600">등록된 활성 수취 계좌 없음</p>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase">
            <Building2 size={14} /> 활성 수수료 수취
          </div>
          {active.fee_settle ? (
            <div className="mt-2">
              <p className="font-semibold text-slate-900">
                {active.fee_settle.bankName}{' '}
                <span className="font-mono">{active.fee_settle.accountNumber}</span>
              </p>
              <p className="text-sm text-slate-500">
                {active.fee_settle.accountHolder} · 잔액 ₩
                {Number(active.fee_settle.lastBalance || 0).toLocaleString()}
                {active.fee_settle.purpose !== 'fee_settle' ? ' (수취 계좌 폴백)' : ''}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-sm text-rose-600">등록된 활성 수수료 계좌 없음</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-slate-900">등록 계좌</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <RefreshCw size={14} /> 새로고침
          </button>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white"
          >
            <Plus size={14} /> 계좌 등록
          </button>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm grid gap-3 sm:grid-cols-2"
        >
          <label className="text-sm">
            <span className="text-slate-500">표시명</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="예: 카이로스 중고 수취"
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">용도</span>
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
              value={form.purpose}
              onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
            >
              <option value="marketplace_receive">직영 판매 수취</option>
              <option value="fee_settle">중계 수수료 수취</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-slate-500">은행</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
              value={form.bankName}
              onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">계좌번호</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-mono"
              value={form.accountNumber}
              onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">예금주</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
              value={form.accountHolder}
              onChange={(e) => setForm((f) => ({ ...f, accountHolder: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">연결 유저 UID (선택, VA 동기화)</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
              value={form.linkedUserId}
              onChange={(e) => setForm((f) => ({ ...f, linkedUserId: e.target.value }))}
              placeholder="운영 계정 Firebase UID"
            />
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? '저장 중…' : '등록'}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-4 py-3">용도</th>
              <th className="px-4 py-3">계좌</th>
              <th className="px-4 py-3">잔액</th>
              <th className="px-4 py-3">상태</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                  불러오는 중…
                </td>
              </tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                  등록된 계좌가 없습니다
                </td>
              </tr>
            ) : (
              accounts.map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-medium">{purposeLabel[a.purpose] || a.purpose}</div>
                    <div className="text-xs text-slate-400">{a.label}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      {a.bankName} <span className="font-mono">{a.accountNumber}</span>
                    </div>
                    <div className="text-xs text-slate-400">{a.accountHolder}</div>
                  </td>
                  <td className="px-4 py-3 font-semibold">
                    ₩{Number(a.lastBalance || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    {a.isActive !== false ? (
                      <span className="text-emerald-600">활성</span>
                    ) : (
                      <span className="text-slate-400">비활성</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {a.isActive !== false && (
                      <button
                        type="button"
                        onClick={() => handleDeactivate(a.id)}
                        className="inline-flex items-center gap-1 text-rose-600 text-xs"
                      >
                        <Trash2 size={14} /> 비활성
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold text-slate-900">최근 입금·이체 원장</h3>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3">일시</th>
                <th className="px-4 py-3">유형</th>
                <th className="px-4 py-3">금액</th>
                <th className="px-4 py-3">잔액 후</th>
                <th className="px-4 py-3">메모</th>
              </tr>
            </thead>
            <tbody>
              {ledger.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                    아직 원장이 없습니다
                  </td>
                </tr>
              ) : (
                ledger.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 whitespace-nowrap">{formatTs(row.createdAt)}</td>
                    <td className="px-4 py-2">{row.type || '—'}</td>
                    <td className="px-4 py-2 font-semibold text-amber-700">
                      ₩{Number(row.amountKRW || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">
                      ₩{Number(row.balanceAfter || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 truncate max-w-[220px]">
                      {row.description || row.itemId || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlatformAccountsPanel;
